import React, { useState, useEffect, useContext } from "react";
import { AuthContext } from "../../context/AuthContext";
import api from "../../utils/Api";
import { CalendarDays } from "lucide-react";
import AusenciasEmpleado from "../empleado/AusenciasEmpleado";

const estadoBadge = {
  pendiente: "tn-badge tn-badge--yellow",
  aprobada:  "tn-badge tn-badge--green",
  rechazada: "tn-badge tn-badge--red",
};

const tipoLabel = {
  vacaciones: "Vacaciones",
  enfermedad: "Enfermedad",
  permiso:    "Permiso",
};

export default function AusenciasAdmin() {
  const { token, loading } = useContext(AuthContext);

  const [ausencias, setAusencias]       = useState([]);
  const [filterEstado, setFilterEstado] = useState("");
  const [filterTipo, setFilterTipo]     = useState("");
  const [toast, setToast]               = useState({ show: false, type: "", msg: "" });
  const [gestionModal, setGestionModal] = useState(null); // ausencia seleccionada
  const [notaEmpresa, setNotaEmpresa]   = useState("");
  const [nuevoEstado, setNuevoEstado]   = useState("");
  const [submitting, setSubmitting]     = useState(false);
  const [tab, setTab]                   = useState("equipo"); // "equipo" | "mias"

  const showToast = (type, msg) => {
    setToast({ show: true, type, msg });
    setTimeout(() => setToast({ show: false, type: "", msg: "" }), 4000);
  };

  const loadAusencias = async () => {
    if (!token) return;
    try {
      const params = {};
      if (filterEstado) params.estado = filterEstado;
      if (filterTipo)   params.tipo   = filterTipo;
      const res = await api.get("/ausencias", { params });
      setAusencias(Array.isArray(res.data) ? res.data : []);
    } catch {
      showToast("danger", "Error al cargar las ausencias.");
    }
  };

  useEffect(() => { loadAusencias(); }, [token, filterEstado, filterTipo]);

  const openGestion = (ausencia) => {
    setGestionModal(ausencia);
    setNuevoEstado(ausencia.estado);
    setNotaEmpresa(ausencia.nota_empresa ?? "");
  };

  const handleGestionar = async () => {
    if (!gestionModal) return;
    setSubmitting(true);
    try {
      await api.put(`/ausencias/${gestionModal.id}`, {
        estado:       nuevoEstado,
        nota_empresa: notaEmpresa || null,
      });
      showToast("success", `Ausencia marcada como "${nuevoEstado}"`);
      setGestionModal(null);
      loadAusencias();
    } catch (err) {
      showToast("danger", err.message || "Error al actualizar la ausencia.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: 200, color: "var(--clr-muted)", fontSize: 14 }}>
      Cargando…
    </div>
  );

  // Contador de pendientes para el subtítulo
  const pendientes = ausencias.filter(a => a.estado === "pendiente").length;

  return (
    <div>
      {toast.show && <div className={`tn-toast tn-toast--${toast.type}`}>{toast.msg}</div>}

      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 4, flexWrap: "wrap", gap: 8 }}>
        <div>
          <h1 className="tn-page-title">Ausencias y vacaciones</h1>
          <p className="tn-page-sub" style={{ marginBottom: 0 }}>
            {tab === "equipo"
              ? "Gestiona las solicitudes de ausencia de tu equipo"
              : "Solicita y consulta tus propias ausencias"}
            {tab === "equipo" && pendientes > 0 && (
              <span className="tn-badge tn-badge--yellow" style={{ marginLeft: 10 }}>
                {pendientes} pendiente{pendientes !== 1 ? "s" : ""}
              </span>
            )}
          </p>
        </div>
      </div>

      {/* Pestañas */}
      <div style={{ display: "flex", gap: 4, marginBottom: 20, borderBottom: "1px solid var(--clr-border)" }}>
        {[
          { key: "equipo", label: "Equipo" },
          { key: "mias",   label: "Mis ausencias" },
        ].map(({ key, label }) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            style={{
              background: "none",
              border: "none",
              borderBottom: tab === key ? "2px solid var(--clr-accent)" : "2px solid transparent",
              color: tab === key ? "var(--clr-accent)" : "var(--clr-muted)",
              fontWeight: tab === key ? 700 : 400,
              fontSize: 14,
              padding: "8px 16px",
              cursor: "pointer",
              marginBottom: -1,
              transition: "color .15s, border-color .15s",
            }}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === "mias" ? (
        <AusenciasEmpleado />
      ) : (
        <>
          {/* Filtros */}
          <div style={{ display: "flex", gap: 12, marginBottom: 20, flexWrap: "wrap", alignItems: "center" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <label className="tn-label" style={{ margin: 0 }}>Estado</label>
              <select className="tn-input tn-select" style={{ width: "auto" }}
                value={filterEstado} onChange={(e) => setFilterEstado(e.target.value)}>
                <option value="">Todos</option>
                <option value="pendiente">Pendiente</option>
                <option value="aprobada">Aprobada</option>
                <option value="rechazada">Rechazada</option>
              </select>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <label className="tn-label" style={{ margin: 0 }}>Tipo</label>
              <select className="tn-input tn-select" style={{ width: "auto" }}
                value={filterTipo} onChange={(e) => setFilterTipo(e.target.value)}>
                <option value="">Todos</option>
                <option value="vacaciones">Vacaciones</option>
                <option value="enfermedad">Enfermedad</option>
                <option value="permiso">Permiso</option>
              </select>
            </div>
            <span style={{ fontSize: 13, color: "var(--clr-muted)", marginLeft: "auto" }}>
              {ausencias.length} resultado{ausencias.length !== 1 ? "s" : ""}
            </span>
          </div>

          {/* Tabla */}
          <div className="tn-card">
            {ausencias.length === 0 ? (
              <div style={{ padding: "60px 20px", textAlign: "center", color: "var(--clr-muted)", fontSize: 14 }}>
                No hay ausencias para mostrar
              </div>
            ) : (
              <div className="tn-table-wrap">
                <table className="tn-table">
                  <thead>
                    <tr>
                      <th>Empleado</th>
                      <th>Tipo</th>
                      <th>Desde</th>
                      <th>Hasta</th>
                      <th>Días hábiles</th>
                      <th>Nota trabajador</th>
                      <th>Estado</th>
                      <th>Acción</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ausencias.map((a) => (
                      <tr key={a.id}>
                        <td data-label="Empleado">
                          <span style={{ fontWeight: 600 }}>{a.usuario?.nombre || "—"}</span>
                          {a.usuario?.apellidos && (
                            <span style={{ color: "var(--clr-muted)", marginLeft: 4 }}>{a.usuario.apellidos}</span>
                          )}
                        </td>
                        <td data-label="Tipo">{tipoLabel[a.tipo] ?? a.tipo}</td>
                        <td data-label="Desde">
                          {new Date(a.fecha_inicio + "T00:00:00").toLocaleDateString("es-ES")}
                        </td>
                        <td data-label="Hasta">
                          {new Date(a.fecha_fin + "T00:00:00").toLocaleDateString("es-ES")}
                        </td>
                        <td data-label="Días hábiles">
                          <span style={{ display: "inline-flex", alignItems: "center", gap: 4, fontWeight: 600, color: "var(--clr-accent)" }}>
                            <CalendarDays size={14} />
                            {a.dias_habiles ?? "—"}
                          </span>
                        </td>
                        <td data-label="Nota trabajador" style={{ color: "var(--clr-muted)", fontSize: 13 }}>
                          {a.nota_trabajador || "—"}
                        </td>
                        <td data-label="Estado">
                          <span className={estadoBadge[a.estado] ?? "tn-badge tn-badge--slate"}>
                            {a.estado}
                          </span>
                        </td>
                        <td data-label="Acción">
                          <button
                            className="tn-btn tn-btn--ghost"
                            style={{ padding: "5px 12px", fontSize: 12 }}
                            onClick={() => openGestion(a)}
                          >
                            Gestionar
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {/* Modal de gestión */}
      {gestionModal && (
        <div className="tn-modal-backdrop" onClick={() => setGestionModal(null)}>
          <div className="tn-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 460 }}>
            <div className="tn-modal__header">
              <span style={{ fontWeight: 700, fontSize: 15 }}>Gestionar ausencia</span>
              <button onClick={() => setGestionModal(null)}
                style={{ background: "none", border: "none", cursor: "pointer", color: "var(--clr-muted)", fontSize: 20, lineHeight: 1 }}>×</button>
            </div>
            <div className="tn-modal__body">
              {/* Resumen de la ausencia */}
              <div style={{ background: "var(--clr-bg)", borderRadius: 8, padding: "12px 14px", marginBottom: 20, fontSize: 13 }}>
                <div style={{ fontWeight: 700, marginBottom: 4 }}>
                  {gestionModal.usuario?.nombre} {gestionModal.usuario?.apellidos}
                </div>
                <div style={{ color: "var(--clr-muted)" }}>
                  {tipoLabel[gestionModal.tipo] ?? gestionModal.tipo} ·{" "}
                  {new Date(gestionModal.fecha_inicio + "T00:00:00").toLocaleDateString("es-ES")} →{" "}
                  {new Date(gestionModal.fecha_fin + "T00:00:00").toLocaleDateString("es-ES")}
                </div>
                {gestionModal.dias_habiles != null && (
                  <div style={{ color: "var(--clr-accent)", fontWeight: 600, marginTop: 4 }}>
                    <CalendarDays size={13} style={{ marginRight: 4, verticalAlign: "middle" }} />
                    {gestionModal.dias_habiles} día{gestionModal.dias_habiles !== 1 ? "s" : ""} hábil{gestionModal.dias_habiles !== 1 ? "es" : ""}
                  </div>
                )}
                {gestionModal.nota_trabajador && (
                  <div style={{ marginTop: 8, padding: "8px 10px", background: "#FFF7ED", borderRadius: 6, color: "var(--clr-text)", fontStyle: "italic" }}>
                    "{gestionModal.nota_trabajador}"
                  </div>
                )}
              </div>

              <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                <div>
                  <label className="tn-label">Nuevo estado</label>
                  <select className="tn-input tn-select" value={nuevoEstado}
                    onChange={(e) => setNuevoEstado(e.target.value)}>
                    <option value="pendiente">Pendiente</option>
                    <option value="aprobada">Aprobada</option>
                    <option value="rechazada">Rechazada</option>
                  </select>
                </div>
                <div>
                  <label className="tn-label">Nota de respuesta (opcional)</label>
                  <textarea className="tn-input tn-textarea" rows="3"
                    placeholder="Motivo de aprobación o rechazo…"
                    value={notaEmpresa} onChange={(e) => setNotaEmpresa(e.target.value)} />
                </div>
              </div>
            </div>
            <div className="tn-modal__footer">
              <button className="tn-btn tn-btn--ghost" onClick={() => setGestionModal(null)}>Cancelar</button>
              <button
                className={`tn-btn ${nuevoEstado === "aprobada" ? "tn-btn--success" : nuevoEstado === "rechazada" ? "tn-btn--danger" : "tn-btn--primary"}`}
                onClick={handleGestionar}
                disabled={submitting}
              >
                {submitting ? "Guardando…" : `Marcar como ${nuevoEstado}`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
